import type { ConversationMessage } from '@/components/session/ConversationTranscript';
import type { ConversationMessageEvent, ConversationMessageUpdateEvent } from './streamingClient';

/**
 * Live-transcript state transitions shared by the dictation, conversation and
 * embed pages, so the three recording surfaces apply ASR events identically.
 */

/** ASR placeholders for non-speech audio; never shown or appended as transcript. */
export const isNonSpeechMarker = (text: string): boolean =>
  text === '[silence]' || text === '[lyklaborð]' || text === '[background noise]';

/** A Phase-1 segment awaiting its Phase-2 rewrite, and where its text starts in the transcript. */
export interface PendingSegment {
  text: string;
  position: number;
}

/**
 * Keep pending positions pointing at their text after `delta` characters were
 * inserted (or removed, if negative) at `from`.
 */
export const shiftPendingSegments = (pending: Map<string, PendingSegment>, from: number, delta: number): void => {
  if (delta === 0) return;
  for (const segment of pending.values()) {
    if (segment.position >= from) segment.position += delta;
  }
};

/**
 * Forget every pending position after a manual edit: an edit can move text in
 * ways a position can't follow (e.g. changing a prefix so an earlier copy of a
 * repeated phrase lands on a later segment's old offset). Refinements then go
 * through the unique-match search instead.
 */
export const invalidatePendingPositions = (pending: Map<string, PendingSegment>): void => {
  for (const segment of pending.values()) segment.position = -1;
};

const WORD_CHAR = /[\p{L}\p{N}]/u;

/** Offsets where `needle` occurs as whole words, so "hné" never matches inside "hnéð". */
const wordOccurrences = (haystack: string, needle: string): number[] => {
  const found: number[] = [];
  for (let at = haystack.indexOf(needle); at !== -1; at = haystack.indexOf(needle, at + 1)) {
    const before = haystack[at - 1];
    const after = haystack[at + needle.length];
    const startsWord = !WORD_CHAR.test(needle[0]) || !before || !WORD_CHAR.test(before);
    const endsWord = !WORD_CHAR.test(needle[needle.length - 1]) || !after || !WORD_CHAR.test(after);
    if (startsWord && endsWord) found.push(at);
  }
  return found;
};

/**
 * Splice a Phase-2 refinement over the Phase-1 text it replaces.
 *
 * The recorded position is tried first (-1 once an edit invalidated it). On a
 * miss the text is searched for, but only a single whole-word occurrence is
 * accepted: with repeats we can't tell which one the segment was, and rewriting
 * the wrong sentence is worse than leaving the Phase-1 text. Returns null when
 * the refinement can't be placed, otherwise the new text and where it landed.
 */
export const applyPhase2Refinement = (
  current: string,
  oldText: string,
  newText: string,
  position: number
): { text: string; at: number } | null => {
  const splice = (at: number) => ({
    text: current.substring(0, at) + newText + current.substring(at + oldText.length),
    at,
  });
  if (position >= 0 && current.substring(position, position + oldText.length) === oldText) return splice(position);
  const matches = wordOccurrences(current, oldText);
  return matches.length === 1 ? splice(matches[0]) : null;
};

/**
 * Returns a runner that performs only the first effect handed to it. Create one
 * per event, outside the state updater, so ref bookkeeping done inside the
 * updater happens once even when React re-invokes it (StrictMode, re-run render).
 */
export const createOnceGuard = (): ((effect: () => void) => void) => {
  let done = false;
  return (effect) => {
    if (done) return;
    done = true;
    effect();
  };
};

/**
 * A dictation smart-review result replaces the whole transcript. Mid-recording
 * results are dropped (the pause/stop pass carries the full text), and while
 * paused it is applied only if nobody has edited since `baselineText`.
 */
export const shouldApplyDictationSmartReview = ({
  isDictating,
  isPaused,
  currentText,
  baselineText,
}: {
  isDictating: boolean;
  isPaused: boolean;
  currentText: string;
  baselineText: string;
}): boolean => !isDictating && (!isPaused || currentText.trimEnd() === baselineText.trimEnd());

/** Append a final conversation message, replacing it if the id was already delivered. */
export const upsertConversationMessage = (
  prev: ConversationMessage[],
  event: ConversationMessageEvent,
  fallbackTimestamp: number
): ConversationMessage[] => {
  const { message } = event;
  const next: ConversationMessage = {
    id: message.id,
    speaker: message.speaker,
    text: message.text,
    timestamp: message.timestamp ?? fallbackTimestamp,
    isPendingRefinement: event.is_pending_refinement ?? false,
  };
  return prev.some((m) => m.id === message.id) ? prev.map((m) => (m.id === message.id ? next : m)) : [...prev, next];
};

/** Apply a Phase-2 refinement of one conversation message; always clears its pending marker. */
export const applyConversationMessageUpdate = (
  prev: ConversationMessage[],
  event: ConversationMessageUpdateEvent
): ConversationMessage[] =>
  prev.map((m) =>
    m.id === event.message_id
      ? {
          ...m,
          // An empty refinement would blank the turn and drop it from the transcript.
          text: event.text || m.text,
          speaker: event.speaker === 'doctor' || event.speaker === 'patient' ? event.speaker : m.speaker,
          isPendingRefinement: event.is_pending_refinement,
          wasJustRefined: event.was_refined ?? false,
        }
      : m
  );

/** Clear the refinement highlight once its animation has played. */
export const clearJustRefined = (prev: ConversationMessage[], messageId: string): ConversationMessage[] =>
  prev.map((m) => (m.id === messageId ? { ...m, wasJustRefined: false } : m));

/** Checkpoints written before turns carried timestamps restore without one; default it so sorting stays numeric. */
export const restoreConversationMessages = (turns: unknown[]): ConversationMessage[] =>
  (turns as ConversationMessage[]).map((t) => ({ ...t, timestamp: typeof t.timestamp === 'number' ? t.timestamp : 0 }));
